/**
 * Contexto de sesión: credenciales, empresa seleccionada y perfil del usuario.
 *
 * La sesión guarda dos cosas:
 *
 * - **Credenciales**: la empresa elegida y, en modo local, usuario y contraseña.
 *   Con Keycloak no hay contraseña: la identidad va en el token
 *   (`auth/keycloak.ts`).
 * - **Perfil** (permisos por empresa): con él la interfaz decide qué pantallas y
 *   botones mostrar. `puede(permiso)` es la única vía por la que se consulta, y
 *   responde **para la empresa elegida**: con Keycloak se puede tener un perfil
 *   distinto en cada empresa.
 *
 * El contexto de empresa del apdo. 12.1 proviene de la selección de la pantalla
 * de acceso o de la barra superior; el backend lo exige en cada llamada y
 * comprueba el permiso en esa empresa.
 */

import { createContext, useCallback, useContext, useMemo, useState } from 'react'
import type { ReactNode } from 'react'

import {
  guardarCredenciales,
  guardarPerfil,
  leerCredenciales,
  leerPerfil,
  olvidarCredenciales,
  olvidarPerfil,
  type Credenciales,
  type Perfil,
  type Permiso,
} from '../api/cliente'
import { cerrarSesionCorporativa, conKeycloak, usuarioCorporativo } from '../auth/keycloak'

/**
 * Perfil guardado, si es de quien tiene la sesión corporativa abierta. En un
 * puesto compartido otra persona puede haber entrado en Keycloak en la misma
 * pestaña: su perfil no se hereda.
 */
function perfilInicial(): Perfil | null {
  const guardado = leerPerfil()
  if (guardado && conKeycloak && guardado.username !== usuarioCorporativo()) {
    olvidarPerfil()
    olvidarCredenciales()
    return null
  }
  return guardado
}

interface ValorSesion {
  credenciales: Credenciales | null
  perfil: Perfil | null
  entrar: (credenciales: Credenciales, perfil: Perfil) => void
  salir: () => void
  cambiarEmpresa: (empresaId: string) => void
  /** True si el usuario tiene el permiso en la empresa elegida. */
  puede: (permiso: Permiso) => boolean
  /** Refresca el perfil sin volver a la pantalla de acceso. */
  actualizarPerfil: (perfil: Perfil) => void
}

const Contexto = createContext<ValorSesion | null>(null)

export function ProveedorSesion({ children }: { children: ReactNode }) {
  const [perfil, setPerfil] = useState<Perfil | null>(() => perfilInicial())
  const [credenciales, setCredenciales] = useState<Credenciales | null>(() => leerCredenciales())

  const entrar = useCallback((nuevas: Credenciales, nuevoPerfil: Perfil) => {
    guardarCredenciales(nuevas)
    guardarPerfil(nuevoPerfil)
    setCredenciales(nuevas)
    setPerfil(nuevoPerfil)
  }, [])

  const salir = useCallback(() => {
    olvidarCredenciales()
    olvidarPerfil()
    setCredenciales(null)
    setPerfil(null)
    // Con Keycloak se cierra también la sesión corporativa (vuelve al login).
    if (conKeycloak) cerrarSesionCorporativa()
  }, [])

  const cambiarEmpresa = useCallback((empresaId: string) => {
    setCredenciales((actuales) => {
      if (!actuales) return actuales
      const nuevas = { ...actuales, empresaId }
      guardarCredenciales(nuevas)
      return nuevas
    })
  }, [])

  const actualizarPerfil = useCallback((nuevoPerfil: Perfil) => {
    guardarPerfil(nuevoPerfil)
    setPerfil(nuevoPerfil)
  }, [])

  const puede = useCallback(
    (permiso: Permiso) => {
      if (perfil === null) return false
      const enEmpresa = credenciales?.empresaId
        ? perfil.permisos_por_empresa?.[credenciales.empresaId]
        : undefined
      if (enEmpresa) return enEmpresa.includes(permiso)
      return perfil.es_administrador || perfil.permisos.includes(permiso)
    },
    [perfil, credenciales?.empresaId],
  )

  const valor = useMemo(
    () => ({ credenciales, perfil, entrar, salir, cambiarEmpresa, puede, actualizarPerfil }),
    [credenciales, perfil, entrar, salir, cambiarEmpresa, puede, actualizarPerfil],
  )

  return <Contexto.Provider value={valor}>{children}</Contexto.Provider>
}

export function useSesion(): ValorSesion {
  const valor = useContext(Contexto)
  if (!valor) throw new Error('useSesion debe usarse dentro de ProveedorSesion')
  return valor
}
